import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { loadApiSetting, ApiNotConfiguredNotice, fetchNewsDataDirect } from "./apiSettingsHelpers";

/* GFI Nexus — Market News (fix-dashboard.md "Fase C.4", later replaced
   GDELT with NewsData.io, then narrowed to fresh-produce/Mexico-US
   import relevance per a follow-up fix-dashboard.md pass)
   ─────────────────────────────────────────────────────────────────
   Two independent sources, merged into one feed:
   - NewsData.io: fetched directly from the browser via
     fetchNewsDataDirect (apiSettingsHelpers.js) — CORS-enabled per
     spec, better free tier (200 req/day) than GDELT, but requires an
     API key (GDELT didn't). Its api_name slug stays "gdelt" — see
     ApiSettingsRegistry.js's DEFAULT_APIS comment on why the slug
     wasn't renamed when the provider was swapped (avoids orphaning an
     already-configured Supabase row for this slot).
     FREE-TIER CONSTRAINTS (fix-dashboard.md, discovered the hard way
     across three bug reports):
       - `category` over-narrows combined with country+language to ~0
         results — removed.
       - a long OR-joined `q` (multiple keywords in one request) trips
         422 Unprocessable Entity past ~100 chars — fixed by moving to
         ONE fixed general query instead of per-keyword requests.
       - `from_date` (historical range) is a paid-tier feature — ANY
         from_date on /latest returns 422 on the free tier. Removed
         entirely; /latest always returns roughly the last 48h,
         regardless of what range the user has selected.
       - `country=us,mx` in a single request is also rejected — the
         free tier allows exactly one country per request. Fixed by
         two separate requests (fetchNewsDataForCountry: "us", "mx"),
         combined client-side.
     Net result: exactly 2 NewsData.io requests per refresh
     (NEWSDATA_COUNTRIES), each with NEWSDATA_GENERAL_QUERY, no
     from_date. The 24h/7d/30d range selector can't make NewsData look
     further back than its native ~48h — instead it's applied as a
     client-side date filter over whatever NewsData/Google News
     actually returned (see withinRange below); a UI notice explains
     this when 7d/30d is selected. A daily request counter
     (NEWSDATA_USAGE_KEY) tracks real network calls against the
     200/day free-tier ceiling — cache hits (30-minute TTL) don't
     count.
   - Google News RSS: news.google.com does NOT send CORS headers for
     arbitrary origins, so this one stays routed through
     netlify/functions/api-proxy.js's mode:"fetch" passthrough
     (server-to-server, no CORS involved) and parsed as XML here via
     DOMParser.

   RELEVANCE FILTERING (fix-dashboard.md): raw results from both
   sources are noisy — general politics, sports, unrelated regions.
   After merge+dedupe, isExcluded() drops off-topic items by keyword
   (client-side, so "Clear filters" can restore the unfiltered feed
   instantly without a re-fetch) and classifyBlock()/isPriority() sort
   what's left into Alerts/Market/Industry with priority items on top
   of each block. */

/* User-editable keyword chips — these now drive ONLY Google News RSS's
   OR-joined query (that source has no per-keyword request cost and
   does support real date-range filtering). NewsData.io no longer
   queries per-keyword at all (see NEWSDATA_GENERAL_QUERY below, fixed
   fix-dashboard.md 422 fix) so adding/removing a chip has no effect on
   NewsData request volume. */
var DEFAULT_KEYWORDS = [
  "Mexico berry export", "USDA tariff produce", "California berry freeze",
  "Michoacan agriculture", "US border produce", "Driscoll's",
];

/* NewsData.io free tier: one fixed general query, one request per
   country (fix-dashboard.md — see file header). Exactly 2 requests per
   refresh, independent of the keyword chips above and of range. */
var NEWSDATA_GENERAL_QUERY = "berry OR produce OR USDA OR tariff OR freeze";
var NEWSDATA_COUNTRIES = ["us", "mx"];

var AUTO_REFRESH_MS = 60 * 60 * 1000;
var GNEWS_WHEN = { "24h": "1d", "7d": "7d", "30d": "30d" };

var NEWSDATA_CACHE_KEY = "gfi-newsdata-cache";
/* 30 minutes (was 15) — fix-dashboard.md, now that a request covers a
   fixed general query per country rather than a specific keyword, a
   longer TTL costs less free-tier quota without losing much freshness. */
var NEWSDATA_CACHE_TTL_MS = 30 * 60 * 1000;
var NEWSDATA_MIN_INTERVAL_MS = 2000;
/* Module-level (not component state) — the throttle needs to survive
   remounts within the same page load, not just re-renders. */
var lastNewsDataCallAt = 0;

var NEWSDATA_USAGE_KEY = "gfi-newsdata-usage";
var NEWSDATA_DAILY_LIMIT = 200;
function todayStr() { return new Date().toISOString().substring(0, 10); }
/* Counts REAL network calls only — a cache hit costs nothing against
   the free tier and doesn't increment this. Resets automatically on a
   new calendar day (UTC, matches todayStr's own ISO date). */
function recordNewsDataUsage() {
  try {
    var raw = JSON.parse(localStorage.getItem(NEWSDATA_USAGE_KEY) || "{}");
    var today = todayStr();
    var count = raw.date === today ? (raw.count || 0) + 1 : 1;
    localStorage.setItem(NEWSDATA_USAGE_KEY, JSON.stringify({ date: today, count: count }));
    return count;
  } catch (e) { return null; }
}
function getNewsDataUsageToday() {
  try {
    var raw = JSON.parse(localStorage.getItem(NEWSDATA_USAGE_KEY) || "{}");
    return raw.date === todayStr() ? (raw.count || 0) : 0;
  } catch (e) { return 0; }
}

/* Client-side relevance filter (fix-dashboard.md CAMBIO 2, loosened by
   a follow-up fix-dashboard.md bug report — "los filtros descartan el
   100% de resultados"). Applied after merge+dedupe so toggling "Clear
   filters" is instant. */
var EXCLUDE_KEYWORDS = [
  "shooting", "murder", "arrest", "terrorist", "kidnap",
  "ukraine war", "gaza", "israel war", "russia invasion",
  "football", "soccer", "baseball", "nfl", "nba", "celebrity", "hollywood", "movie premiere",
];
/* election/campaign/vote used to be unconditional excludes — too
   aggressive (an "election" hit anywhere nuked the item even in an
   otherwise on-topic tariff/trade story). Now conditional: only
   excluded if the item ALSO reads like war/crime coverage. */
var CONDITIONAL_POLITICAL_KEYWORDS = ["election", "campaign", "vote"];
var CONDITIONAL_POLITICAL_TRIGGERS = ["war", "police"];
/* "china economy"/"india tech" removed entirely — too narrow a phrase
   match to be worth the risk of a false-positive exclusion; the
   region words below still exclude when unaccompanied by a produce/
   berry/tariff mention. */
var EXCLUDE_REGIONS = ["pakistan", "iran", "saudi"];
/* Inclusive override (fix-dashboard.md "Priorización inclusiva") — a
   match here bypasses EVERY exclude rule above, not just the region
   one. Checked first in isExcluded() below. */
var ALWAYS_SHOW_KEYWORDS = [
  "berry", "blueberry", "blackberry", "raspberry", "produce", "usda", "tariff", "freeze",
  "hurricane", "mexico", "california", "florida", "georgia", "michoacan", "jalisco",
  "organic", "harvest", "agriculture", "export", "import", "customs", "wholesale",
];

/* CAMBIO 3 — priority keywords/regions: items matching these sort to
   the top of their block instead of pure recency. */
var PRIORITY_KEYWORDS = [
  "berry", "blueberry", "blackberry", "raspberry", "avocado", "produce", "agriculture",
  "usda", "tariff", "freeze", "hurricane pacific", "mexico export", "us import",
  "customs", "organic certification", "wholesale", "retail price", "harvest", "crop",
  "supply chain", "logistics",
];
var PRIORITY_REGIONS = ["mexico", "texas", "california", "florida", "georgia", "michoacan", "jalisco", "los reyes de salgado"];

/* CAMBIO 5 — the 3 feed blocks. Checked in this order: Alerts first
   (highest signal, wins even if a market/industry keyword also
   matches), then Market, then Industry as the general fallback bucket
   for anything that passed the exclude filter but didn't hit a more
   specific list. */
var ALERT_KEYWORDS = ["freeze", "hurricane", "tariff", "ban", "recall", "border closure", "border closed"];
var MARKET_KEYWORDS = ["price", "prices", "trend", "demand", "supply", "wholesale", "retail", "market", "harvest", "crop yield", "export volume", "import volume", "cost"];
var INDUSTRY_KEYWORDS = ["usda", "regulation", "regulación", "certification", "organic", "customs", "agriculture policy", "compliance", "inspection", "food safety", "agriculture"];

function textOf(item) {
  return ((item.title || "") + " " + (item.snippet || "")).toLowerCase();
}
/* Returns "always" (inclusive override matched, never excluded),
   "keyword"/"political"/"region" (which exclude rule tripped), or null
   (not excluded). One shared classifier so the actual filter
   (isExcluded) and the diagnostic stage-by-stage counter below can't
   drift out of sync with each other. */
function classifyExclusion(item) {
  var text = textOf(item);
  if (ALWAYS_SHOW_KEYWORDS.some(function (k) { return text.indexOf(k) !== -1; })) return "always";
  if (EXCLUDE_KEYWORDS.some(function (k) { return text.indexOf(k) !== -1; })) return "keyword";
  var hasPolitical = CONDITIONAL_POLITICAL_KEYWORDS.some(function (k) { return text.indexOf(k) !== -1; });
  var hasTrigger = CONDITIONAL_POLITICAL_TRIGGERS.some(function (k) { return text.indexOf(k) !== -1; });
  if (hasPolitical && hasTrigger) return "political";
  if (EXCLUDE_REGIONS.some(function (k) { return text.indexOf(k) !== -1; })) return "region";
  return null;
}
function isExcluded(item) {
  var reason = classifyExclusion(item);
  return reason !== null && reason !== "always";
}
function isPriority(item) {
  var text = textOf(item);
  return PRIORITY_KEYWORDS.some(function (k) { return text.indexOf(k) !== -1; })
    || PRIORITY_REGIONS.some(function (k) { return text.indexOf(k) !== -1; });
}
function classifyBlock(item) {
  var text = textOf(item);
  if (ALERT_KEYWORDS.some(function (k) { return text.indexOf(k) !== -1; })) return "alert";
  if (MARKET_KEYWORDS.some(function (k) { return text.indexOf(k) !== -1; })) return "market";
  if (INDUSTRY_KEYWORDS.some(function (k) { return text.indexOf(k) !== -1; })) return "industry";
  return "industry";
}

var T = {
  en: {
    title: "Market News", subtitle: "NewsData.io + Google News, filtered for fresh-produce and Mexico–US import relevance.",
    addKeyword: "Add keyword…", refresh: "Refresh", refreshing: "Loading…", retry: "Retry",
    range24h: "Last 24h", range7d: "Last 7 days", range30d: "Last 30 days",
    readMore: "Read more →", noResults: "No news found for the current keywords/range.",
    loadError: "Could not load news.", updated: "Updated",
    notConfiguredNewsData: "NewsData.io", notConfiguredGnews: "Google News RSS",
    clearFilters: "Clear filters", applyFilters: "Apply filters",
    alertsBlock: "🚨 Alerts", marketBlock: "📊 Market", industryBlock: "🌱 Industry",
    counter: function (shown, total, filtered) { return "Showing " + shown + " of " + total + " news (" + filtered + " filtered)"; },
    fallbackNotice: "No results with filters applied — showing the general feed instead.",
    viewAllUnfiltered: "View all, unfiltered",
    newsDataUsage: function (used) { return "NewsData: " + used + " of " + NEWSDATA_DAILY_LIMIT + " requests used today"; },
    newsDataDegraded: function (errorCount, total) { return "NewsData.io: " + errorCount + " of " + total + " country queries failed — showing Google News + whatever NewsData did return."; },
    extendedRangeNotice: "NewsData.io only shows the free tier's last ~48h — the extended range only applies to Google News RSS.",
    noDescription: "No description available.",
  },
  es: {
    title: "Noticias de Mercado", subtitle: "NewsData.io + Google News, filtrado por relevancia a fruta fresca e importación México–EE.UU.",
    addKeyword: "Agregar keyword…", refresh: "Actualizar", refreshing: "Cargando…", retry: "Reintentar",
    range24h: "Últimas 24h", range7d: "Últimos 7 días", range30d: "Últimos 30 días",
    readMore: "Leer más →", noResults: "No hay noticias para los keywords/rango actuales.",
    loadError: "No se pudieron cargar las noticias.", updated: "Actualizado",
    notConfiguredNewsData: "NewsData.io", notConfiguredGnews: "Google News RSS",
    clearFilters: "Quitar filtros", applyFilters: "Aplicar filtros",
    alertsBlock: "🚨 Alertas", marketBlock: "📊 Mercado", industryBlock: "🌱 Industria",
    counter: function (shown, total, filtered) { return "Mostrando " + shown + " de " + total + " noticias (" + filtered + " filtradas)"; },
    fallbackNotice: "Sin resultados con filtros — mostrando feed general.",
    viewAllUnfiltered: "Ver todas sin filtros",
    newsDataUsage: function (used) { return "NewsData: " + used + " de " + NEWSDATA_DAILY_LIMIT + " requests usados hoy"; },
    newsDataDegraded: function (errorCount, total) { return "NewsData.io: " + errorCount + " de " + total + " consultas por país fallaron — mostrando Google News + lo que NewsData sí devolvió."; },
    extendedRangeNotice: "NewsData muestra últimas 48h del free tier — el rango extendido aplica solo a Google News RSS.",
    noDescription: "Sin descripción disponible.",
  },
};

/* fix-dashboard.md — NewsData.io (and occasionally Google News RSS)
   descriptions/titles can carry raw HTML (<p>, <span>, <img>, entities)
   that isn't safe or readable rendered as plain text. Deliberately a
   simple regex strip, NOT dangerouslySetInnerHTML (that would just
   move the problem from "shows tags" to "executes them") and not a
   DOMParser round-trip either — this runs on every mapped item from
   both sources, so keeping it a cheap synchronous regex matters more
   than handling exotic entities the free-tier feeds are unlikely to
   send. Applied once at mapping time (fetchNewsDataForCountry /
   fetchGoogleNewsRss) so it also cleans up the text used for dedupe
   and keyword filtering, not just what's rendered. */
var HTML_ENTITY_MAP = { "&amp;": "&", "&nbsp;": " ", "&quot;": "\"", "&#39;": "'", "&apos;": "'", "&lt;": "<", "&gt;": ">" };
function cleanText(raw) {
  if (!raw) return "";
  var text = String(raw).replace(/<[^>]*>/g, " ");
  text = text.replace(/&amp;|&nbsp;|&quot;|&#39;|&apos;|&lt;|&gt;/g, function (m) { return HTML_ENTITY_MAP[m]; });
  text = text.replace(/&#(\d+);/g, function (_, code) { return String.fromCharCode(parseInt(code, 10)); });
  text = text.replace(/&#x([0-9a-fA-F]+);/g, function (_, hex) { return String.fromCharCode(parseInt(hex, 16)); });
  return text.replace(/\s+/g, " ").trim();
}
function truncateText(text, maxLen) {
  if (!text || text.length <= maxLen) return text;
  return text.slice(0, maxLen).trim() + "...";
}

function fmtWhen(dateVal, lang) {
  try {
    var d = new Date(dateVal);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString(lang === "es" ? "es-MX" : "en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (e) { return ""; }
}

function readNewsDataCache() {
  try { return JSON.parse(localStorage.getItem(NEWSDATA_CACHE_KEY) || "{}") || {}; } catch (e) { return {}; }
}
function writeNewsDataCache(cache) {
  try { localStorage.setItem(NEWSDATA_CACHE_KEY, JSON.stringify(cache)); } catch (e) { /* quota — best-effort, cache is a nice-to-have */ }
}
function pruneNewsDataCache(cache) {
  var now = Date.now();
  var next = {};
  Object.keys(cache).forEach(function (k) {
    if (cache[k] && (now - cache[k].ts) < NEWSDATA_CACHE_TTL_MS) next[k] = cache[k];
  });
  return next;
}

/* Single-country NewsData.io /latest call — free tier allows exactly
   ONE country per request and rejects `from_date` outright (see file
   header), so this always fetches NEWSDATA_GENERAL_QUERY with no date
   param; results are whatever NewsData's /latest naturally returns
   (roughly the last 48h). Cached per country for 30 minutes; on a
   cache miss, waits out the shared 2s throttle window
   (lastNewsDataCallAt) then fires and records real usage against the
   daily counter. Never throws — a failure (422, network, invalid key)
   is caught by the caller (fetchNewsData below) so one country's
   failure doesn't sink the other. */
async function fetchNewsDataForCountry(cfg, country, force) {
  var cache = pruneNewsDataCache(readNewsDataCache());
  var cacheKey = "general::" + country;
  if (!force && cache[cacheKey]) return cache[cacheKey].data;

  var waitMs = NEWSDATA_MIN_INTERVAL_MS - (Date.now() - lastNewsDataCallAt);
  if (waitMs > 0) await new Promise(function (resolve) { setTimeout(resolve, waitMs); });
  lastNewsDataCallAt = Date.now();

  var path = "/latest?apikey=" + encodeURIComponent(cfg.apiKey)
    + "&q=" + encodeURIComponent(NEWSDATA_GENERAL_QUERY)
    + "&country=" + country + "&language=en,es&size=10";
  console.log("[MarketNews] NewsData.io request — country:", country, "query:", NEWSDATA_GENERAL_QUERY, "(no from_date — free tier doesn't support it)");
  var data = await fetchNewsDataDirect(path, { endpointUrl: cfg.endpointUrl });
  var usageToday = recordNewsDataUsage();
  var mapped = (data.results || []).map(function (r) {
    return {
      title: cleanText(r.title || ""),
      source: r.source_id || "NewsData.io",
      date: r.pubDate || "",
      url: r.link || "",
      snippet: cleanText(r.description || ""),
      image: r.image_url || "",
    };
  });
  console.log("[MarketNews] NewsData.io returned", mapped.length, "results for country=" + country + " (usage today:", usageToday, "/", NEWSDATA_DAILY_LIMIT + ")");

  cache[cacheKey] = { data: mapped, ts: Date.now() };
  writeNewsDataCache(cache);
  return mapped;
}

/* Runs fetchNewsDataForCountry once per NEWSDATA_COUNTRIES entry
   (2 total: us, mx), sequentially — the shared 2s throttle inside
   fetchNewsDataForCountry already spaces the real requests out. Each
   country is isolated in its own try/catch: a 422 or any other
   per-country failure is logged and skipped rather than aborting the
   batch. Returns { items, errorCount, erroredCountries } so the
   caller can show a friendly degraded-source notice when one/both
   countries failed.
   `force` (fix-dashboard.md "botón Refresh no hace nada visible"):
   the manual Refresh button passes true to bypass the 30-minute cache
   entirely, so clicking it always fires a real network request even
   if the cache is still fresh — the hourly AUTO_REFRESH_MS interval
   does NOT pass force, so it still respects the cache as before. */
async function fetchNewsData(cfg, force) {
  if (force) console.log("[MarketNews] cache bypassed");
  var items = [];
  var errorCount = 0;
  var erroredCountries = [];
  for (var i = 0; i < NEWSDATA_COUNTRIES.length; i++) {
    var country = NEWSDATA_COUNTRIES[i];
    try {
      var results = await fetchNewsDataForCountry(cfg, country, force);
      items = items.concat(results);
    } catch (e) {
      errorCount++;
      erroredCountries.push(country);
      console.warn("[MarketNews] NewsData.io request failed for country \"" + country + "\":", e && e.message ? e.message : e);
    }
  }
  return { items: items, errorCount: errorCount, erroredCountries: erroredCountries };
}

/* Client-side range filter (fix-dashboard.md — NewsData's free tier
   can't be asked for a historical window, so the 24h/7d/30d selector
   is applied here instead, over whatever NewsData/Google News
   actually returned). Unparsable dates are kept rather than dropped —
   a malformed pubDate shouldn't silently disappear an otherwise valid
   item. */
function withinRange(item, range) {
  var days = range === "24h" ? 1 : range === "30d" ? 30 : 7;
  var cutoff = Date.now() - days * 86400000;
  var d = new Date(item.date);
  if (isNaN(d.getTime())) return true;
  return d.getTime() >= cutoff;
}

/* Google News RSS — routed through the Netlify proxy (CORS). Parses
   the returned XML with DOMParser. Exported (2026-10-13) so
   WeatherWatchRegistry.js's "Send Alert" feature can pull a couple of
   real, linked headlines into the alert email without duplicating
   this XML-parsing logic — this is a pure fetch/calc helper, not a UI
   component, so it's exempt from this app's usual per-file
   duplication convention for Field/Section/Badge-style components. */
export async function fetchGoogleNewsRss(keywords, range) {
  var query = "(" + keywords.map(function (k) { return "\"" + k + "\""; }).join(" OR ") + ") when:" + (GNEWS_WHEN[range] || "7d");
  var res = await fetch("/.netlify/functions/api-proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ apiName: "google_news_rss", mode: "fetch", query: query }),
  });
  var data = await res.json();
  if (!data.ok) throw new Error(data.error || "proxy fetch failed");
  var xml = new window.DOMParser().parseFromString(data.body, "text/xml");
  var items = Array.from(xml.querySelectorAll("item"));
  /* Google News RSS descriptions carry HTML markup. `endpointUrl` is
     admin-editable in Settings, so this isn't a fully trusted source
     by construction — parse the description as HTML and take only
     textContent (never render it as markup) to rule out script
     injection via a reconfigured/malicious endpoint. */
  return items.map(function (item) {
    var titleRaw = (item.querySelector("title") || {}).textContent || "";
    var sourceEl = item.querySelector("source");
    var descRaw = (item.querySelector("description") || {}).textContent || "";
    var descDoc = new window.DOMParser().parseFromString(descRaw, "text/html");
    /* Google News RSS rarely carries an image, but when it does it's a
       namespaced <media:thumbnail url="..."> or a plain <enclosure
       url="...">. getElementsByTagName (not querySelector, which needs
       escaped-colon CSS syntax for namespaced tags) handles both. */
    var mediaThumb = item.getElementsByTagName("media:thumbnail")[0];
    var enclosure = item.getElementsByTagName("enclosure")[0];
    var image = (mediaThumb && mediaThumb.getAttribute("url")) || (enclosure && enclosure.getAttribute("url")) || "";
    return {
      title: cleanText(titleRaw),
      source: (sourceEl && sourceEl.textContent) || "Google News",
      date: (item.querySelector("pubDate") || {}).textContent || "",
      url: (item.querySelector("link") || {}).textContent || "",
      /* descDoc.body.textContent already strips markup (DOMParser
         parsed descRaw as HTML above) — cleanText() here is a second
         pass for stray literal tags/entities that survive as text
         (fix-dashboard.md CAMBIO 5), not the primary strip. */
      snippet: cleanText((descDoc.body && descDoc.body.textContent) || ""),
      image: image,
    };
  });
}

/* fix-dashboard.md — thumbnail with a graceful no-image / broken-image
   fallback. `errored` starts false and flips permanently true on the
   img's onError (404, CORS-blocked, timeout, etc.); once flipped, the
   placeholder renders instead and no further load is attempted —
   never leaves an empty hole in the card. */
function NewsThumbnail({ src }) {
  var [errored, setErrored] = useState(false);
  var showImg = !!src && !errored;
  return (
    <div style={{ width: 80, height: 80, flexShrink: 0, borderRadius: 4, overflow: "hidden", background: "var(--color-bg)", border: "1px solid var(--color-border)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {showImg ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={function () { setErrored(true); }}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <span style={{ fontSize: 26, opacity: 0.35 }}>📰</span>
      )}
    </div>
  );
}

function NewsItem({ item, lang, alert }) {
  var t = T[lang === "es" ? "es" : "en"];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-start", padding: "12px 0", borderBottom: "1px solid var(--color-border)" }}>
      <NewsThumbnail src={item.image} />
      {/* flex-basis forces a wrap onto its own line (thumbnail on top)
         once the card is too narrow for image + text side by side —
         no hand-written @media breakpoint needed (fix-dashboard.md
         CAMBIO 5), matching this codebase's existing preference for
         flex/grid-driven responsiveness over explicit breakpoints
         (see FobPricesRegistry.js). */}
      <div style={{ flex: "1 1 180px", minWidth: 180 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: alert ? "#DC2626" : "var(--color-text)" }}>{item.title}</div>
        <div style={{ fontSize: 10, color: "var(--color-text-muted)", margin: "3px 0" }}>{item.source} · {fmtWhen(item.date, lang)}</div>
        <div style={{ fontSize: 11, color: item.snippet ? "var(--color-text-secondary)" : "var(--color-text-muted)", fontStyle: item.snippet ? "normal" : "italic", marginBottom: 4 }}>
          {item.snippet ? truncateText(item.snippet, 200) : t.noDescription}
        </div>
        {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "var(--color-accent)", fontWeight: 600, textDecoration: "none" }}>{t.readMore}</a>}
      </div>
    </div>
  );
}

function NewsBlock({ title, items, lang, alert }) {
  if (items.length === 0) return null;
  return (
    <div style={{ background: "var(--color-bg-card)", border: "1px solid " + (alert ? "rgba(220,38,38,0.35)" : "var(--color-border)"), borderRadius: 10, padding: "4px 16px", marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, padding: "10px 0 6px", color: alert ? "#DC2626" : "var(--color-text-secondary)" }}>{title}</div>
      {items.map(function (item, i) { return <NewsItem key={i} item={item} lang={lang} alert={alert} />; })}
    </div>
  );
}

export default function MarketNewsRegistry({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang === "es" ? "es" : "en"];

  var [keywords, setKeywords] = useState(DEFAULT_KEYWORDS.slice());
  var [kwInput, setKwInput] = useState("");
  var [range, setRange] = useState("7d");
  var [filtersEnabled, setFiltersEnabled] = useState(true);

  var [newsDataCfg, setNewsDataCfg] = useState(null);
  var [gnewsCfg, setGnewsCfg] = useState(null);
  var [loadingCfg, setLoadingCfg] = useState(true);

  var [rawItems, setRawItems] = useState([]);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState(false);
  var [updatedAt, setUpdatedAt] = useState("");
  var [newsDataDegraded, setNewsDataDegraded] = useState(null);
  var [usageToday, setUsageToday] = useState(function () { return getNewsDataUsageToday(); });
  var aliveRef = useRef(true);

  useEffect(function () {
    aliveRef.current = true;
    Promise.all([loadApiSetting("gdelt"), loadApiSetting("google_news_rss")]).then(function (arr) {
      if (!aliveRef.current) return;
      setNewsDataCfg(arr[0]);
      setGnewsCfg(arr[1]);
      setLoadingCfg(false);
    });
    return function () { aliveRef.current = false; };
  }, []);

  var fetchAll = useCallback(async function (ndCfg, nCfg, kws, rng, force) {
    if (!(ndCfg && ndCfg.configured) && !(nCfg && nCfg.configured)) return;
    setLoading(true); setError(false); setNewsDataDegraded(null);
    try {
      var results = [];
      var settled = await Promise.allSettled([
        (ndCfg && ndCfg.configured) ? fetchNewsData(ndCfg, force) : Promise.resolve({ items: [], errorCount: 0, erroredCountries: [] }),
        (nCfg && nCfg.configured) ? fetchGoogleNewsRss(kws, rng) : Promise.resolve([]),
      ]);
      var ndSettled = settled[0], gnewsSettled = settled[1];
      var ndResult = ndSettled.status === "fulfilled" ? ndSettled.value : { items: [], errorCount: 0, erroredCountries: [] };
      if (ndSettled.status === "fulfilled") results = results.concat(ndResult.items);
      if (gnewsSettled.status === "fulfilled") results = results.concat(gnewsSettled.value);
      /* fix-dashboard.md CAMBIO — a 422 (or any other) per-country
         failure degrades gracefully to whatever DID succeed (the other
         NewsData country + Google News) instead of blowing up the
         whole feed; surface it as a small notice rather than silence. */
      if (ndCfg && ndCfg.configured && ndResult.errorCount > 0) {
        setNewsDataDegraded({ errorCount: ndResult.errorCount, total: NEWSDATA_COUNTRIES.length });
      }
      /* Dedupe by URL first, falling back to title — NewsData and
         Google News frequently pick up the same wire story, sometimes
         via different tracking URLs for an identical headline. */
      var seenUrl = {}, seenTitle = {};
      var deduped = results.filter(function (it) {
        var urlKey = (it.url || "").trim().toLowerCase();
        var titleKey = (it.title || "").trim().toLowerCase();
        if (!urlKey && !titleKey) return false;
        if (urlKey && seenUrl[urlKey]) return false;
        if (titleKey && seenTitle[titleKey]) return false;
        if (urlKey) seenUrl[urlKey] = true;
        if (titleKey) seenTitle[titleKey] = true;
        return true;
      });
      /* fix-dashboard.md CAMBIO 1 — NewsData's free tier can't be
         asked for a historical range (see file header); apply the
         user's 24h/7d/30d choice as a post-fetch date filter instead,
         over everything both sources actually returned. */
      deduped = deduped.filter(function (it) { return withinRange(it, rng); });
      deduped.sort(function (a, b) { return new Date(b.date) - new Date(a.date); });
      console.log("[MarketNews] merged+deduped+range-filtered (NewsData.io + Google News) total:", deduped.length, "sample titles:", deduped.slice(0, 5).map(function (m) { return m.title; }));
      console.log("[MarketNews] fetched " + deduped.length + " articles");
      if (!aliveRef.current) return;
      setRawItems(deduped);
      setUsageToday(getNewsDataUsageToday());
      setUpdatedAt(new Date().toLocaleTimeString(lang === "es" ? "es-MX" : "en-US", { hour: "2-digit", minute: "2-digit" }));
      if (deduped.length === 0 && results.length === 0 && settled.every(function (r) { return r.status === "rejected"; })) setError(true);
    } catch (e) {
      if (aliveRef.current) setError(true);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [lang]);

  useEffect(function () {
    if (loadingCfg) return;
    fetchAll(newsDataCfg, gnewsCfg, keywords, range);
    var id = setInterval(function () { fetchAll(newsDataCfg, gnewsCfg, keywords, range); }, AUTO_REFRESH_MS);
    return function () { clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCfg, newsDataCfg, gnewsCfg, keywords, range]);

  /* filtersEnabled toggles the client-side exclude list only (CAMBIO
     6) — no re-fetch needed, so this is a pure derivation of rawItems.
     Also logs a per-stage breakdown (fix-dashboard.md diagnostic item
     2: how many survive each exclusion rule) so a future "filters
     ate everything" report can be diagnosed from the console without
     re-instrumenting. */
  var filteredResult = useMemo(function () {
    if (!filtersEnabled) return { items: rawItems, alwaysShown: 0, excludedKeyword: 0, excludedPolitical: 0, excludedRegion: 0 };
    var alwaysShown = 0, excludedKeyword = 0, excludedPolitical = 0, excludedRegion = 0;
    var kept = rawItems.filter(function (it) {
      var reason = classifyExclusion(it);
      if (reason === "always") { alwaysShown++; return true; }
      if (reason === "keyword") { excludedKeyword++; return false; }
      if (reason === "political") { excludedPolitical++; return false; }
      if (reason === "region") { excludedRegion++; return false; }
      return true;
    });
    return { items: kept, alwaysShown: alwaysShown, excludedKeyword: excludedKeyword, excludedPolitical: excludedPolitical, excludedRegion: excludedRegion };
  }, [rawItems, filtersEnabled]);

  useEffect(function () {
    if (!filtersEnabled || rawItems.length === 0) return;
    console.log(
      "[MarketNews] filter diagnostics — raw:", rawItems.length,
      "| kept:", filteredResult.items.length,
      "| always-show override matched:", filteredResult.alwaysShown,
      "| excluded by keyword (crime/war/sports/entertainment):", filteredResult.excludedKeyword,
      "| excluded by conditional political (election/campaign/vote + war/police):", filteredResult.excludedPolitical,
      "| excluded by region (pakistan/iran/saudi):", filteredResult.excludedRegion
    );
  }, [rawItems, filtersEnabled, filteredResult]);

  /* fix-dashboard.md CAMBIO 5 — if filters ate 100% of a non-empty raw
     feed, fall back to showing everything unfiltered rather than a
     dead "no results" screen, and say so. */
  var fallbackActive = filtersEnabled && rawItems.length > 0 && filteredResult.items.length === 0;
  var displayItems = fallbackActive ? rawItems : filteredResult.items;

  var blocks = useMemo(function () {
    var groups = { alert: [], market: [], industry: [] };
    displayItems.forEach(function (it) { groups[classifyBlock(it)].push(it); });
    Object.keys(groups).forEach(function (k) {
      groups[k] = groups[k].slice().sort(function (a, b) {
        var pa = isPriority(a) ? 1 : 0, pb = isPriority(b) ? 1 : 0;
        if (pa !== pb) return pb - pa;
        return new Date(b.date) - new Date(a.date);
      });
    });
    return groups;
  }, [displayItems]);

  var addKeyword = function () {
    var v = kwInput.trim();
    if (!v || keywords.indexOf(v) !== -1) return;
    setKeywords(keywords.concat([v]));
    setKwInput("");
  };
  var removeKeyword = function (kw) {
    setKeywords(keywords.filter(function (k) { return k !== kw; }));
  };

  /* fix-dashboard.md "botón Refresh no hace nada visible" — the bug
     wasn't that Refresh did nothing, it's that a cache hit inside the
     30-minute TTL made it LOOK like nothing happened (same data back
     near-instantly, and the button itself had no loading state at
     all). force=true here bypasses that cache (see fetchNewsData); the
     `disabled`+spinner on the button below is the missing visual
     feedback while the fetch is in flight. */
  var handleManualRefresh = function () {
    console.log("[MarketNews] manual refresh triggered");
    fetchAll(newsDataCfg, gnewsCfg, keywords, range, true);
  };

  if (loadingCfg) return null;

  var neitherConfigured = !(newsDataCfg && newsDataCfg.configured) && !(gnewsCfg && gnewsCfg.configured);
  /* Counter reflects the ACTUAL filter outcome (filteredResult), not
     displayItems — during a fallback, displayItems == rawItems (all
     shown) but the counter should still say what filtering itself
     found, with the fallback banner explaining why it was overridden. */
  var filteredOutCount = rawItems.length - filteredResult.items.length;

  return (
    <div style={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto", paddingRight: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
          <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: 0 }}>{t.subtitle}</p>
          {!neitherConfigured && (newsDataCfg && newsDataCfg.configured) && (
            <p style={{ fontSize: 9.5, color: "var(--color-text-muted)", margin: "3px 0 0" }}>{t.newsDataUsage(usageToday)}</p>
          )}
        </div>
        {!neitherConfigured && (
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={function () { setFiltersEnabled(!filtersEnabled); }} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: filtersEnabled ? "transparent" : "var(--color-accent-bg, rgba(42,122,111,0.1))", color: filtersEnabled ? "var(--color-text-secondary)" : "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              {filtersEnabled ? t.clearFilters : t.applyFilters}
            </button>
            <button type="button" onClick={handleManualRefresh} disabled={loading} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1, fontSize: 12, fontWeight: 600 }}>
              {loading && <span style={{ display: "inline-block", animation: "mn-spin 0.8s linear infinite" }}>⟳</span>}
              {loading ? t.refreshing : t.refresh}
            </button>
          </div>
        )}
      </div>
      <style>{"@keyframes mn-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }"}</style>

      {neitherConfigured ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <ApiNotConfiguredNotice apiDisplayName={t.notConfiguredNewsData} lang={lang} />
          <ApiNotConfiguredNotice apiDisplayName={t.notConfiguredGnews} lang={lang} />
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {keywords.map(function (kw) {
              return (
                <span key={kw} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 14, fontSize: 11, fontWeight: 600, background: "var(--color-accent-bg, rgba(42,122,111,0.1))", color: "var(--color-accent)", border: "1px solid var(--color-border)" }}>
                  {kw}
                  <span onClick={function () { removeKeyword(kw); }} style={{ cursor: "pointer", fontWeight: 700 }}>×</span>
                </span>
              );
            })}
            <input
              type="text"
              value={kwInput}
              onChange={function (e) { setKwInput(e.target.value); }}
              onKeyDown={function (e) { if (e.key === "Enter") addKeyword(); }}
              placeholder={t.addKeyword}
              style={{ padding: "4px 10px", borderRadius: 14, fontSize: 11, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-text)", outline: "none", minWidth: 140 }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 6 }}>
              {[["24h", t.range24h], ["7d", t.range7d], ["30d", t.range30d]].map(function (r) {
                return (
                  <button key={r[0]} type="button" onClick={function () { setRange(r[0]); }} style={{
                    padding: "5px 12px", borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: "pointer",
                    border: range === r[0] ? "1px solid var(--color-accent)" : "1px solid var(--color-border)",
                    background: range === r[0] ? "var(--color-accent-bg, rgba(42,122,111,0.1))" : "transparent",
                    color: range === r[0] ? "var(--color-accent)" : "var(--color-text-secondary)",
                  }}>{r[1]}</button>
                );
              })}
            </div>
            {rawItems.length > 0 && (
              <span style={{ fontSize: 10.5, color: "var(--color-text-muted)" }}>{t.counter(filteredResult.items.length, rawItems.length, filteredOutCount)}</span>
            )}
          </div>

          {range !== "24h" && (newsDataCfg && newsDataCfg.configured) && (
            <div style={{ padding: "7px 14px", borderRadius: 8, background: "rgba(107,114,128,0.08)", border: "1px solid var(--color-border)", color: "var(--color-text-muted)", fontSize: 10.5, marginBottom: 10 }}>
              ℹ {t.extendedRangeNotice}
            </div>
          )}

          {newsDataDegraded && (
            <div style={{ padding: "9px 14px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)", color: "#B45309", fontSize: 11.5, marginBottom: 10 }}>
              ⚠ {t.newsDataDegraded(newsDataDegraded.errorCount, newsDataDegraded.total)}
            </div>
          )}

          {fallbackActive && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, padding: "9px 14px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)", color: "#B45309", fontSize: 11.5, marginBottom: 12 }}>
              <span>⚠ {t.fallbackNotice}</span>
              <button type="button" onClick={function () { setFiltersEnabled(false); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,119,6,0.4)", background: "transparent", color: "#B45309", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.viewAllUnfiltered}</button>
            </div>
          )}

          {loading && rawItems.length === 0 && (
            <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: "14px 16px", fontSize: 12, color: "var(--color-text-muted)" }}>{t.refreshing}</div>
          )}
          {!loading && error && (
            <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 8 }}>{t.loadError}</div>
              <button type="button" onClick={function () { fetchAll(newsDataCfg, gnewsCfg, keywords, range); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.retry}</button>
            </div>
          )}
          {!error && !loading && displayItems.length === 0 && rawItems.length >= 0 && (
            <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: "14px 16px", fontSize: 12, color: "var(--color-text-muted)" }}>{t.noResults}</div>
          )}

          <NewsBlock title={t.alertsBlock} items={blocks.alert} lang={lang} alert />
          <NewsBlock title={t.marketBlock} items={blocks.market} lang={lang} alert={false} />
          <NewsBlock title={t.industryBlock} items={blocks.industry} lang={lang} alert={false} />

          {updatedAt && <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 8 }}>{t.updated}: {updatedAt}</div>}
        </>
      )}
    </div>
  );
}
